/**
 * Shared progress schema — frontend half.
 *
 * Structural mirror of `services/prompt-orchestrator/app/v2/progress/types.py`.
 * Reducer parity tests assert that both implementations apply the same
 * event sequence to the same output state.
 */

export type TaskState =
  | 'queued'
  | 'running'
  | 'done'
  | 'needs_input'
  | 'failed';

export const PHASE_IDS = [
  'mapping',
  'quality_review',
  'correction',
  'assembly',
  'export',
] as const;
export type PhaseId = (typeof PHASE_IDS)[number];

export const PHASE_LABELS: Record<PhaseId, string> = {
  mapping: 'Mapping',
  quality_review: 'Quality Review',
  correction: 'Correction',
  assembly: 'Assembly',
  export: 'Export',
};

export const EVENT_KINDS = [
  'phase_start',
  'phase_done',
  'task_start',
  'task_progress',
  'task_done',
  'task_failed',
  'task_needs_input',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export interface ProgressTask {
  id: string;
  label: string;
  state: TaskState;
  /** Ephemeral current sub-step; overwritten by task_progress, cleared on task_done. */
  activity?: string | null;
  metrics?: Record<string, unknown> | null;
  reason?: string | null;
}

export interface ProgressPhase {
  id: string;
  label: string;
  state: TaskState;
  tasks: ProgressTask[];
  total: number;
  done: number;
  running: number;
  failed: number;
}

export interface ProgressState {
  phases: ProgressPhase[];
  /** Monotonic; reducer bumps on every applied event so the UI can dedup. */
  version: number;
  /** ISO-8601 of the last applied event. */
  updated_at: string | null;
}

/**
 * Discriminated by `kind`. Other fields are required per-kind:
 *   phase_start / phase_done:        phase_id [+ label]
 *   task_start:                      phase_id, task_id, label
 *   task_progress:                   phase_id, task_id, activity
 *   task_done:                       phase_id, task_id [+ metrics]
 *   task_failed / task_needs_input:  phase_id, task_id, reason
 */
export interface ProgressEvent {
  kind: EventKind;
  phase_id: string;
  task_id?: string | null;
  label?: string | null;
  activity?: string | null;
  metrics?: Record<string, unknown> | null;
  reason?: string | null;
}
